import {
  signInWithPhoneNumber,
  RecaptchaVerifier,
  signOut as firebaseSignOut
} from 'firebase/auth'
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore'
import { auth, db } from './firebase'
import store from '../store'

/**
 * Initialize reCAPTCHA verifier
 * @param {string} containerId - ID of the container element for reCAPTCHA
 * @returns {RecaptchaVerifier}
 */
export function initRecaptchaVerifier(containerId = 'recaptcha-container') {
  // Clear any existing verifier on the window object
  if (window.recaptchaVerifier) {
    window.recaptchaVerifier.clear()
  }

  // Create new RecaptchaVerifier with the element ID
  window.recaptchaVerifier = new RecaptchaVerifier(
    containerId,
    {
      size: 'invisible',
      callback: () => {
        // reCAPTCHA solved
      },
      'expired-callback': () => {
        // Response expired
        console.warn('reCAPTCHA expired')
      }
    },
    auth
  )

  return window.recaptchaVerifier
}

/**
 * Send OTP to phone number
 * @param {string} phoneE164 - Phone number in E.164 format
 * @param {RecaptchaVerifier} recaptchaVerifier - reCAPTCHA verifier instance
 * @returns {Promise<ConfirmationResult>}
 */
export async function sendOTP(phoneE164, recaptchaVerifier) {
  try {
    store.dispatch('setLoading', { key: 'otpSend', value: true })
    store.dispatch('setError', null)

    const confirmationResult = await signInWithPhoneNumber(
      auth,
      phoneE164,
      recaptchaVerifier
    )

    return confirmationResult
  } catch (error) {
    console.error('Error sending OTP:', error)

    // Handle specific error codes
    let errorMessage = 'Failed to send OTP. Please try again.'

    if (error.code === 'auth/invalid-phone-number') {
      errorMessage = 'Invalid phone number format.'
    } else if (error.code === 'auth/too-many-requests') {
      errorMessage = 'Too many requests. Please try again later.'
    } else if (error.code === 'auth/quota-exceeded') {
      errorMessage = 'SMS quota exceeded. Please try again later.'
    }

    store.dispatch('setError', errorMessage)
    throw error
  } finally {
    store.dispatch('setLoading', { key: 'otpSend', value: false })
  }
}

/**
 * Verify OTP code
 * @param {ConfirmationResult} confirmationResult - Result from sendOTP
 * @param {string} code - OTP code entered by user
 * @returns {Promise<UserCredential>}
 */
export async function verifyOTP(confirmationResult, code) {
  try {
    store.dispatch('setLoading', { key: 'otpVerify', value: true })
    store.dispatch('setError', null)

    const result = await confirmationResult.confirm(code)
    return result
  } catch (error) {
    console.error('Error verifying OTP:', error)

    let errorMessage = 'Failed to verify OTP. Please try again.'

    if (error.code === 'auth/invalid-verification-code') {
      errorMessage = 'Incorrect code. Please try again.'
    } else if (error.code === 'auth/code-expired') {
      errorMessage = 'Code expired. Please request a new one.'
    }

    store.dispatch('setError', errorMessage)
    throw error
  } finally {
    store.dispatch('setLoading', { key: 'otpVerify', value: false })
  }
}

/**
 * Resolve user document in Firestore after authentication
 * @param {string} uid - User ID
 * @param {string} mode - 'signup' or 'login'
 * @param {string} phoneE164 - Phone number in E.164 format
 * @param {string} name - User name (required for signup, optional for login)
 * @returns {Promise<object>} User document
 */
export async function resolveUserDoc(uid, mode, phoneE164, name = '') {
  try {
    store.dispatch('setLoading', { key: 'userDoc', value: true })
    store.dispatch('setError', null)

    const userRef = doc(db, 'users', uid)
    const userSnap = await getDoc(userRef)

    if (mode === 'signup') {
      // For signup, always create/update the document
      const userData = {
        user_id: uid,
        mobile_number: phoneE164 || auth.currentUser?.phoneNumber || '',
        name: name || '',
        updated_at: serverTimestamp(),
        last_login_at: serverTimestamp()
      }

      // Only set created_at if document doesn't exist
      if (!userSnap.exists()) {
        userData.created_at = serverTimestamp()
        userData.isOptInMessageSent = false
        userData.optInMessageSentAt = null
      }

      await setDoc(userRef, userData, { merge: true })
    } else if (mode === 'login') {
      // For login
      if (userSnap.exists()) {
        // Update existing user
        await updateDoc(userRef, {
          updated_at: serverTimestamp(),
          last_login_at: serverTimestamp()
        })
      } else {
        // Create new user document if it doesn't exist
        await setDoc(userRef, {
          user_id: uid,
          mobile_number: phoneE164 || auth.currentUser?.phoneNumber || '',
          name: '',
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
          last_login_at: serverTimestamp()
        })
      }
    }

    // Fetch the updated document
    const updatedSnap = await getDoc(userRef)
    const userDoc = updatedSnap.data()

    // Store in Vuex
    store.dispatch('setUserDoc', userDoc)
    store.dispatch('setAuthState', 'authenticated')

    // Set pending profile flag for signup mode
    if (mode === 'signup') {
      store.dispatch('setSignupContext', { pendingProfile: true })
    }

    return userDoc
  } catch (error) {
    console.error('Error resolving user document:', error)
    store.dispatch('setError', 'Failed to save user data. Please try again.')
    throw error
  } finally {
    store.dispatch('setLoading', { key: 'userDoc', value: false })
  }
}

/**
 * Load user document from Firestore
 * @param {string} uid - User ID
 * @returns {Promise<object|null>} User document or null
 */
export async function loadUserDoc(uid) {
  try {
    store.dispatch('setLoading', { key: 'userDoc', value: true })

    const userRef = doc(db, 'users', uid)
    const userSnap = await getDoc(userRef)

    if (userSnap.exists()) {
      const userDoc = userSnap.data()
      store.dispatch('setUserDoc', userDoc)
      return userDoc
    }

    return null
  } catch (error) {
    console.error('Error loading user document:', error)
    return null
  } finally {
    store.dispatch('setLoading', { key: 'userDoc', value: false })
  }
}

/**
 * Update user profile with optional fields
 * @param {object} profileData - Profile data to update (company_name, email)
 * @returns {Promise<void>}
 */
export async function updateUserProfile(profileData) {
  try {
    const currentUser = auth.currentUser
    if (!currentUser) {
      throw new Error('No authenticated user')
    }

    const userRef = doc(db, 'users', currentUser.uid)

    // Prepare update data
    const updateData = {
      updated_at: serverTimestamp()
    }

    // Add optional fields if provided
    if ('company_name' in profileData) {
      updateData.company_name = profileData.company_name || ''
    }

    if ('email' in profileData) {
      updateData.email = profileData.email || ''
    }

    // Update document
    await updateDoc(userRef, updateData)

    // Reload user document to update store
    await loadUserDoc(currentUser.uid)
  } catch (error) {
    console.error('Error updating user profile:', error)
    throw error
  }
}

/**
 * Sign out user
 */
export async function signOut() {
  try {
    await firebaseSignOut(auth)
    store.dispatch('clearAuthState')
  } catch (error) {
    console.error('Error signing out:', error)
    throw error
  }
}
